import type { ReactNode } from 'react';

import type {
  ApiRequestedAttachment,
  ApiRequestedFileAttachment,
} from '#/api/types/ApiRequestedAttachment';

import { RequestedAcknowledgementField } from './RequestedAcknowledgementField';
import { RequestedMultipleOptionField } from './RequestedMultipleOptionField';
import { RequestedPhoneUserField } from './RequestedPhoneUserField';
import { RequestedSingleOptionField } from './RequestedSingleOptionField';
import { RequestedTextUserField } from './RequestedTextUserField';
import { RequestedURLUserField } from './RequestedURLUserField';

export type RenderRequestedAttachmentFileFieldFn = (data: {
  key: string;
  name: string;
  label: string;
  disabled: boolean;
  requestedFileAttachment: ApiRequestedFileAttachment;
}) => ReactNode;

type Props = {
  index: number;
  requestedAttachment: ApiRequestedAttachment;
  renderRequestedAttachmentFileField: RenderRequestedAttachmentFileFieldFn;
  allFieldsRequired: boolean;
  disabled: boolean;
  defaultCountry: string | null | undefined;
};

export function ListingRequestedAttachmentsFieldSwitch({
  index,
  requestedAttachment,
  renderRequestedAttachmentFileField,
  allFieldsRequired,
  disabled,
  defaultCountry,
}: Props) {
  const label =
    !allFieldsRequired && requestedAttachment.isRequired
      ? `${requestedAttachment.label}*`
      : requestedAttachment.label;

  const key = [requestedAttachment.type, index].join('-');
  const name = `requestedAttachments.${index}`;

  if (requestedAttachment.type === 'TEXT') {
    return (
      <RequestedTextUserField
        key={[requestedAttachment.type, index].join('-')}
        name={name}
        label={label}
        disabled={disabled}
      />
    );
  }
  if (requestedAttachment.type === 'URL') {
    return (
      <RequestedURLUserField
        key={[requestedAttachment.type, index].join('-')}
        name={name}
        label={
          !allFieldsRequired && requestedAttachment.isRequired
            ? `${requestedAttachment.label}*`
            : requestedAttachment.label
        }
        disabled={disabled}
        placeholder="https://example.org"
      />
    );
  }

  if (requestedAttachment.type === 'FILE') {
    return renderRequestedAttachmentFileField({
      key,
      name,
      label,
      disabled,
      requestedFileAttachment: requestedAttachment,
    });
  }

  if (requestedAttachment.type === 'PHONE') {
    return (
      <RequestedPhoneUserField
        key={[requestedAttachment.type, index].join('-')}
        name={name}
        label={
          !allFieldsRequired && requestedAttachment.isRequired
            ? `${requestedAttachment.label}*`
            : requestedAttachment.label
        }
        disabled={disabled}
        defaultCountry={defaultCountry}
      />
    );
  }

  if (requestedAttachment.type === 'OPTION_SINGLE') {
    return (
      <RequestedSingleOptionField
        key={[requestedAttachment.type, index].join('-')}
        name={name}
        label={
          !allFieldsRequired && requestedAttachment.isRequired
            ? `${requestedAttachment.label}*`
            : requestedAttachment.label
        }
        disabled={disabled}
        options={requestedAttachment.options}
      />
    );
  }

  if (requestedAttachment.type === 'OPTION_MANY') {
    return (
      <RequestedMultipleOptionField
        key={[requestedAttachment.type, index].join('-')}
        name={name}
        label={
          !allFieldsRequired && requestedAttachment.isRequired
            ? `${requestedAttachment.label}*`
            : requestedAttachment.label
        }
        disabled={disabled}
        options={requestedAttachment.options}
      />
    );
  }

  if (requestedAttachment.type === 'ACKNOWLEDGEMENT') {
    return (
      <RequestedAcknowledgementField
        key={[requestedAttachment.type, index].join('-')}
        requestedAttachment={requestedAttachment}
        name={name}
        disabled={disabled}
      />
    );
  }
}
